import * as signalR from "@microsoft/signalr";
import { VanillaOidc } from "@axa-fr/oidc-client";
import { CaseAPI } from "api/axios.config";

export const HUB_STATUS = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
};

const HUB_PATH = "/hubs/notifications";
const NOTIFICATION_EVENT = "ReceiveNotification";
const RECONNECT_DELAYS = [0, 2000, 10000, 30000];
const RESTART_DELAY = 30000;
const MAX_RESTART_ATTEMPTS = 3;

let connection = null;
let restartTimer = null;
let restartAttempts = 0;
let oidcSubscriptionId = null;
let stopped = true;
let status = HUB_STATUS.DISCONNECTED;

const notificationHandlers = new Set();
const statusHandlers = new Set();

const getHubUrl = () => {
  if (!CaseAPI) return null;
  try {
    return new URL(HUB_PATH, CaseAPI).toString();
  } catch (error) {
    return null;
  }
};

const getAccessToken = async () => {
  try {
    const { isTokensValid, tokens } = await VanillaOidc.get().getValidTokenAsync();
    return isTokensValid && tokens ? tokens.accessToken : "";
  } catch (error) {
    return "";
  }
};

const setStatus = (next) => {
  if (status === next) return;
  status = next;
  statusHandlers.forEach((handler) => handler(next));
};

const emitNotification = (payload) => {
  notificationHandlers.forEach((handler) => handler(payload));
};

const retryPolicy = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
    RECONNECT_DELAYS[previousRetryCount] ?? null,
};

const clearRestartTimer = () => {
  if (restartTimer) {
    clearTimeout(restartTimer);
    restartTimer = null;
  }
};

const scheduleRestart = () => {
  if (stopped || restartTimer) return;
  if (restartAttempts >= MAX_RESTART_ATTEMPTS) return;

  restartAttempts++;
  restartTimer = setTimeout(() => {
    restartTimer = null;
    start();
  }, RESTART_DELAY);
};

const start = async () => {
  if (stopped || !connection) return;
  if (connection.state !== signalR.HubConnectionState.Disconnected) return;

  setStatus(HUB_STATUS.CONNECTING);
  try {
    await connection.start();
    restartAttempts = 0;
    setStatus(HUB_STATUS.CONNECTED);
  } catch (error) {
    setStatus(HUB_STATUS.DISCONNECTED);
    scheduleRestart();
  }
};

const subscribeOidcLogout = () => {
  if (oidcSubscriptionId !== null) return;
  try {
    oidcSubscriptionId = VanillaOidc.get().subscribeEvents((name) => {
      if (
        name === VanillaOidc.eventNames.logout_from_same_tab ||
        name === VanillaOidc.eventNames.logout_from_another_tab
      ) {
        disconnect();
      }
    });
  } catch (error) {
    oidcSubscriptionId = null;
  }
};

const unsubscribeOidcLogout = () => {
  if (oidcSubscriptionId === null) return;
  try {
    VanillaOidc.get().removeEventSubscription(oidcSubscriptionId);
  } catch (error) {
    /* oidc already torn down */
  }
  oidcSubscriptionId = null;
};

const build = () => {
  const url = getHubUrl();
  if (!url) return null;

  const instance = new signalR.HubConnectionBuilder()
    .withUrl(url, { accessTokenFactory: getAccessToken, withCredentials: false })
    .withAutomaticReconnect(retryPolicy)
    .configureLogging(signalR.LogLevel.Information)
    .build();

  instance.on(NOTIFICATION_EVENT, emitNotification);
  instance.onreconnecting(() => setStatus(HUB_STATUS.RECONNECTING));
  instance.onreconnected(() => setStatus(HUB_STATUS.CONNECTED));
  instance.onclose(() => {
    setStatus(HUB_STATUS.DISCONNECTED);
    scheduleRestart();
  });

  return instance;
};

export const connect = () => {
  stopped = false;
  restartAttempts = 0;

  if (!connection) {
    connection = build();
    if (!connection) {
      setStatus(HUB_STATUS.DISCONNECTED);
      return;
    }
    subscribeOidcLogout();
  }

  start();
};

export const disconnect = async () => {
  stopped = true;
  clearRestartTimer();
  unsubscribeOidcLogout();

  const current = connection;
  connection = null;
  setStatus(HUB_STATUS.DISCONNECTED);

  if (current) {
    try {
      await current.stop();
    } catch (error) {
      /* already closed */
    }
  }
};

export const subscribeToNotifications = (handler) => {
  notificationHandlers.add(handler);
  return () => notificationHandlers.delete(handler);
};

export const subscribeToStatus = (handler) => {
  statusHandlers.add(handler);
  return () => statusHandlers.delete(handler);
};
